/**
 * Next Best Action resource (CT-5713): the Concierge's suggestions for one
 * record, shown in the purple card under the profile header, and starting the
 * workflow behind one of them.
 *
 * The suggestions are loaded by the Concierge and are not user-editable. No
 * contract has been shared yet: every path is ASSUMED and marked
 * `TODO(CT-5713 / backend)`. Real signatures and snake_case bodies; only the
 * transport is stubbed (`mockTransport.js`).
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { request } from './mockTransport.js';

export {
  __resetMockNextBestActionsForTests,
  __setMockNbaScenario,
} from './__mocks__/nextBestAction.mock.js';

const mapAction = (raw) =>
  raw
    ? {
        type: raw.type ?? null,
        draftId: raw.draft_id ?? raw.draftId ?? null,
        workflowId: raw.workflow_id ?? raw.workflowId ?? null,
      }
    : null;

const mapLink = (raw) => (raw ? { label: raw.label ?? '', url: raw.url ?? null } : null);

const mapStep = (raw = {}) => ({
  id: raw.id ?? '',
  title: raw.title ?? '',
  description: raw.description ?? null,
  startsNow: Boolean(raw.starts_now ?? raw.startsNow ?? false),
});

/**
 * Map one suggestion. The primary action is identified by its `type`, never by
 * the text of a tag; tags carry a `key` for their colour and a `label` to show.
 */
export const mapApiNextBestActionToUi = (raw = {}) => {
  const details = raw.details ?? {};
  const workflow = raw.workflow ?? null;
  return {
    id: raw.id ?? raw.nba_id ?? '',
    title: raw.title ?? '',
    context: raw.context ?? null,
    description: raw.description ?? null,
    summary: raw.summary ?? null,
    isNew: Boolean(raw.is_new ?? raw.isNew ?? false),
    tags: (raw.tags ?? []).map((tag) => ({ key: tag.key ?? '', label: tag.label ?? '' })),
    primaryAction: mapAction(raw.primary_action ?? raw.primaryAction),
    reviewer: raw.reviewer ? { name: raw.reviewer.name ?? '' } : null,
    details: {
      source: mapLink(details.source),
      why: mapLink(details.why),
      ways: (details.ways ?? []).map((way) => ({
        id: way.id ?? '',
        title: way.title ?? '',
        description: way.description ?? null,
        recommended: Boolean(way.recommended),
        action: mapAction(way.action),
      })),
      steps: (details.steps ?? []).map(mapStep),
    },
    workflow: workflow
      ? {
          name: workflow.name ?? '',
          estimatedTime: workflow.estimated_time ?? workflow.estimatedTime ?? null,
          steps: (workflow.steps ?? []).map(mapStep),
          onStart: workflow.on_start ?? workflow.onStart ?? [],
        }
      : null,
  };
};

/** TODO(CT-5713 / backend): `POST /v0/profile/nba/list` with `{ entity_type, entity_id }`. */
export const listNextBestActions = async ({ entityType, entityId } = {}) => {
  const response = await request('/v0/profile/nba/list', { entity_type: entityType, entity_id: entityId });
  return validateApiResponse(response, 'Failed to load the suggestions');
};

/** TODO(CT-5713 / backend): `POST /v0/profile/nba/workflow/start` with `{ entity_type, entity_id, item_id }`. */
export const startNextBestActionWorkflow = async ({ entityType, entityId, itemId } = {}) => {
  const response = await request('/v0/profile/nba/workflow/start', {
    entity_type: entityType,
    entity_id: entityId,
    item_id: itemId,
  });
  return validateApiResponse(response, 'Failed to start the workflow');
};
